import { Injectable, Injector } from '@angular/core';
import { LoadingController, ToastController, NavController, Platform, ModalController, AlertController } from '@ionic/angular';
import { ChatService } from './chat.service';
import { environment } from '../../environments/environment';
import { UpgradeModalComponent } from '../modals/upgrade-modal/upgrade-modal.component';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  loading: HTMLIonLoadingElement;
  isOnline: boolean = false;
  public initialized = false;
  public outstandingRoute: any;
  public defaultAppointmentColor = "#2B99F0";
  public apiUrl = environment.apiUrl;
  public appMode: "client" | "trainer" = "client";

  /** Viewport is below the 992px layout breakpoint used by .mobile-header / .m-hide. */
  isMobileLayout = false;

  constructor(
    private alertCtrl: AlertController,
    private dbService: DatabaseService,
    private injector: Injector,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private platform: Platform,
    private storageService: StorageService,
  ) {
    this.syncMobileLayout();
    this.platform.resize.subscribe(() => this.syncMobileLayout());
  }

  private syncMobileLayout() {
    this.isMobileLayout = typeof window !== 'undefined' && window.innerWidth < 992;
  }

  async presentLoading(message?: string) {
    if (this.loading) {
      return;
    }
    message = message ? message : 'Please wait ...';
    this.loading = await this.loadingCtrl.create({
      message: message
    });
    return this.loading.present(); 
  }

  /**
   * Update loader text. Ionic often does not repaint when assigning `loading.message`,
   * so we dismiss and present a new overlay with the new copy.
   */
  async setLoadingMessage(message: string) {
    if (!this.loading) {
      await this.presentLoading(message);
      return;
    }
    try {
      await this.loading.dismiss();
    } catch {
      // already dismissed
    }
    this.loading = null;
    this.loading = await this.loadingCtrl.create({ message });
    await this.loading.present();
  }

  async hideLoading() {
    if (this.loading) {
      try {
        await this.loading.dismiss();
      } catch (error) {
        // Dismiss can throw during overlay races/navigation; always clear loading state.
      } finally {
        this.loading = null;
      }
    }
  }

  async showToast(message:string){
    if(message){      
      const toast = await this.toastCtrl.create({
        message: message,
        buttons: [
          {
            text: 'Ok',
            role: 'cancel',
            handler: () => {
            }
          }
        ],
        position: 'bottom',
      });
      toast.present();
    }    
  }

  openChat(convoId: string) {
    // Lazy resolve to avoid ChatService ↔ AppService constructor cycle (esbuild/application builder).
    this.injector.get(ChatService).openConversation(convoId);
  }
  
  async showAlert(message: string) {
    const alert = await this.alertCtrl.create({
      message: message,
      buttons: [
        {
          text: 'Ok',
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => { }
        }
      ]
    });
    await alert.present();
  }

  async showToastWithDuration(message: string, duration?: number, color?: "success" | "danger", route?, position: "top" | "bottom" = "top") {
    if(message){      
      const toast = await this.toastCtrl.create({
        message: message,
        buttons: [
          {
            text: route? "View" : "Ok",
            role: 'cancel',
            handler: () => {
              try {
                if(route) {
                  if(route.indexOf("conversation") > -1) {
                    const ending = route.split("/")[2];
                    const convoId = ending.split("?")[0];
                    this.openChat(convoId);
                  } else {
                    this.navCtrl.navigateForward(route);
                  }
                }
              } catch (error) {
              }
            }
          }
        ],
        position: position,
        color: color? color : "light",
        duration: duration? duration : 5000,
      });
      toast.present();
    }    
  }

  showSuccess(message: string) {
    return this.showToastWithDuration(message, null, "success")
  }

  showError(message: string) {
    return this.showToastWithDuration(message, null, "danger")
  }

  getApiURL() {
    return this.apiUrl
  }

  getRouteFromPush(push: any, userId: string): string {


    /* Boards */
    if(push.type === "boardPost" || push.type === "boardPostComment") {
      return `/boards?boardId=${push.referenceId}`
    }

    /* All else */

    let base = null;

    const user = {
      uid: userId
    } 

    if(push.type === "message" && push.referenceId) {
      base = `/conversation/${push.referenceId}`
    }

    if(push.type === "progressReport") {
      if(push.client !== user.uid) {
        // Same query shape as training-plans: programId + action (referenceId is the check-in id).
        base = `/client/${push.client}/check-ins?programId=${push.referenceId}`
      } else {
        base = `/check-in/${push.referenceId}`
      }
    }

    if(push.type === "mealPlan") {
      if(push.client !== user.uid) {
        base = `/client/${push.client}/meal-plans?programId=${push.referenceId}`
      } else {
        base = `/meal-plan/${push.referenceId}`
      }
    }

    if(push.type === "workoutProgram") {
      if(push.client !== user.uid) {
        base = `/client/${push.client}/training-plans?programId=${push.referenceId}`
      } else {
        base = `/workout-program/${push.referenceId}`
      }
    }

    if(push.type === "supplementProtocol") {
      if(push.client !== user.uid) {
        base = `/client/${push.client}/supplement-protocols?programId=${push.referenceId}`
      } else {
        base = `/supplement-protocol/${push.referenceId}`
      }
    }

    if(push.type === "cardioProgram") {
      if(push.client !== user.uid) {
        base = `/client/${push.client}/cardio-programs?programId=${push.referenceId}`
      } else {
        base = `/cardio-program/${push.referenceId}`
      }
    }

    if(push.type === "stripeOnboarding") {
      base = `/billing/stripe-onboarding`
    }

    const action = push.action ? `${base && base.indexOf('?') > -1 ? '&' : '?'}action=${push.action}` : '';
    return base ? base + action : base
  }

  isMobile() {
    return this.platform.is("ios") || this.platform.is("android")
  }

  isMobileApp() {
    return (this.platform.is("ios") || this.platform.is("android")) && !this.platform.is("mobileweb")
  }

  /**
   * Push/deep-links on native often emit several Router NavigationStarts; the app root dismisses all modals on each.
   * Wait this long before presenting deep-link preview modals so that navigation churn finishes first.
   */
  getDeepLinkModalNavigationSettleMs(): number {
    return this.isMobileApp() ? 750 : 0;
  }

  /** Delay after preview is shown before stacking the comment modal (longer on native). */
  getDeepLinkCommentModalDelayMs(): number {
    return this.isMobileApp() ? 750 : 500;
  }

  isDevice(type) {
    return this.platform.is(type)
  }

  getSystemStatus() {
    return this.dbService.getSnapshot(`system/status`);
  }

  getBillingTerms(): Promise<string> {
    return this.dbService.getSnapshot(`system/terms/billing`);
  }

  uploadBeforeAfter(filename: string, data: string): Promise<string> {
    return this.storageService.fbPutString(`beforeAfter/${filename}`, data);
  }

  async showUpgradeModal(target?: string): Promise<string> {
    const modal = await this.modalCtrl.create({
      component: UpgradeModalComponent,
      componentProps: { 
        targetPlan: target
      }
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    return role
  }

  getCurrentAppVersion() {
    return this.dbService.getSnapshot("system/appVersion");
  }

  async dismissAll(modal?: HTMLIonModalElement) {

    // Load top modal
    if(!modal) {
      modal = await this.modalCtrl.getTop();
    }

    if(modal) {
      return new Promise ((resolve) => {
        modal.dismiss().then(async res => {
          const getNewTop = await this.modalCtrl.getTop();
          if (getNewTop) {
            resolve(this.dismissAll(getNewTop));
          } else {
            resolve(true);
          }
        });
      });
    }
  }

  getInstagramLink(instaId: string) {
    return `https://www.instagram.com/${instaId.trim()}/`
  }
}
